import React, { useState, useEffect } from 'react';
import {
  CalendarIcon,
  CalendarDaysIcon,
  MapPinIcon,
  ClockIcon,
  UserGroupIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import EventStructuredData from '../components/EventStructuredData';
import SEO from '../components/SEO';

interface ApiEvent {
  title: string;
  color: string;
  date: string;
  startTime: string;
  endTime: string;
  fullStart: string;
  fullEnd: string;
}

interface ApiResponse {
  success: boolean;
  dateRange: {
    from: string;
    to: string;
  };
  totalEvents: number;
  events: ApiEvent[];
}

const Events: React.FC = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [apiEvents, setApiEvents] = useState<ApiEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [showDayModal, setShowDayModal] = useState(false);
  const [hiddenEventTypes, setHiddenEventTypes] = useState<Set<string>>(new Set());

  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  // Fetch events from API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://intern.hsv-adlershof.berlin/api/calendar/public');
        const data: ApiResponse = await response.json();
        
        if (data.success) {
          setApiEvents(data.events);
          setError(null);
        } else {
          setError('Fehler beim Laden der Termine');
        }
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Termine konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Helper function to check if an event is past
  const isEventPast = (eventDateStr: string) => {
    const [day, month, year] = eventDateStr.split('.');
    const eventDate = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate < today;
  };

  // Main events for display cards
  const mainEvents = [
    {
      id: 1,
      title: 'Ostersuche',
      date: '06',
      month: 'APR',
      year: '2026',
      fullDate: '6. April 2026',
      description: 'Traditionelle Osterfest-Veranstaltung für alle Mitglieder und ihre Familien. Spaß und Spiel für Mensch und Hund.',
      category: 'Vereinsfest'
    },
    {
      id: 2,
      title: 'Sommerfest',
      date: '11',
      month: 'JUL',
      year: '2026',
      fullDate: '11. Juli 2026',
      description: 'Großes Sommerfest mit Grillen, Spielen und gemeinsamen Aktivitäten für die ganze Vereinsgemeinschaft.',
      category: 'Vereinsfest'
    },
    {
      id: 3,
      title: 'Kossepokal',
      date: '19',
      month: 'SEP',
      year: '2026',
      fullDate: '19. September 2026',
      description: 'Interner Vereinswettbewerb als Generalprobe vor den offiziellen Prüfungen. Alle Disziplinen werden geprüft.',
      category: 'Wettkampf'
    },
    {
      id: 4,
      title: 'Weihnachtsfeier',
      date: '',
      month: 'DEZ',
      year: '2026',
      fullDate: 'Dezember 2026',
      description: 'Gemütlicher Jahresausklang mit Glühwein, Plätzchen und der Ehrung besonderer Vereinsleistungen.',
      category: 'Vereinsfest'
    }
  ];

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Convert Sunday (0) to Monday-first system
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  // Get all unique event types with their colors
  const allEventTypes = React.useMemo(() => {
    const typeMap = new Map<string, string>();
    apiEvents.forEach(event => {
      if (!typeMap.has(event.title)) {
        typeMap.set(event.title, event.color);
      }
    });
    return Array.from(typeMap.entries()).map(([title, color]) => ({ title, color }));
  }, [apiEvents]);

  // Toggle event type visibility
  const toggleEventType = (title: string) => {
    setHiddenEventTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(title)) {
        newSet.delete(title);
      } else {
        newSet.add(title);
      }
      return newSet;
    });
  };

  // Get events for a specific day (filtered)
  const getEventsForDay = (day: number) => {
    if (!day) return [];

    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return apiEvents.filter(event => {
      // API gibt ISO-Format zurück: "2026-01-04T00:00:00.000Z"
      // Extrahiere nur den Datumsanteil (YYYY-MM-DD)
      const eventDate = event.date.split('T')[0];
      // Prüfe ob Event-Typ ausgeblendet ist
      if (hiddenEventTypes.has(event.title)) return false;
      return eventDate === dateStr;
    });
  };

  // Open modal for a specific day
  const openDayModal = (day: number) => {
    const events = getEventsForDay(day);
    if (events.length > 0) {
      setSelectedDay(day);
      setShowDayModal(true);
    }
  };

  // Close modal
  const closeDayModal = () => {
    setShowDayModal(false);
    setSelectedDay(null);
  };

  const calendarDays = generateCalendarDays();

  // Get category style for main events
  const getCategoryStyle = (category: string) => {
    switch(category) {
      case 'Vereinsfest':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'Wettkampf':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'Prüfung':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  // Gästetraining Event für Structured Data (jeden Samstag)
  const getNextSaturday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSaturday = (6 - dayOfWeek + 7) % 7 || 7; // Nächster Samstag
    const nextSaturday = new Date(today);
    nextSaturday.setDate(today.getDate() + daysUntilSaturday);
    return nextSaturday;
  };

  const nextSaturday = getNextSaturday();
  const guestTrainingEvent = {
    name: "Gästetraining HSV Berlin-Adlershof",
    description: "Gästetraining für Interessierte. Jeden Samstag von 09:30 bis 11:00 Uhr am Birkenweg in Berlin-Adlershof. Aktuell nur Warteliste.",
    startDate: `${nextSaturday.toISOString().split('T')[0]}T09:30:00+02:00`,
    endDate: `${nextSaturday.toISOString().split('T')[0]}T11:00:00+02:00`,
    location: "HSV Berlin-Adlershof e.V. Trainingsplatz",
    organizer: "HSV Berlin-Adlershof e.V.",
    price: "0"
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background-dark">
      <SEO
        title="Termine & Veranstaltungen"
        description="Termine HSV Berlin-Adlershof: Trainingskalender, Wettkämpfe, DVG-Prüfungen & Vereinsfeste. Gästetraining jeden Samstag 9:30-11:00 Uhr – aktuell nur Warteliste."
        url="/termine"
        breadcrumbs={[
          { name: 'Startseite', url: '/' },
          { name: 'Termine', url: '/termine' }
        ]}
      />
      <EventStructuredData events={[guestTrainingEvent]} />
      {/* Hero Section */}
      <section className="relative h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/facilities/training-field-2.jpg" 
            alt="Veranstaltungen" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="max-w-3xl">
              <h1 className="text-2xl md:text-5xl font-black text-white mb-4 md:mb-6 uppercase">
                Veranstaltungen & Termine
              </h1>
              <p className="text-base md:text-xl text-white/90">
                Alle wichtigen Termine, Wettkämpfe und Vereinsveranstaltungen auf einen Blick
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar Section */}
      <section className="py-10 md:py-20 bg-neutral-50 dark:bg-background-dark">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4 text-neutral-900 dark:text-neutral-100">
              Trainingskalender
            </h2>
            <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400">
              Unsere regelmäßigen Trainingszeiten und Sondertermine
            </p>
          </div>

          <div className="card p-3 md:p-6">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4 md:mb-6 pb-3 md:pb-4 border-b border-neutral-200 dark:border-neutral-700">
              <button
                onClick={() => navigateMonth('prev')}
                className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
              >
                <ChevronLeftIcon className="w-5 h-5 text-neutral-700 dark:text-neutral-300" />
              </button>
              <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                {monthNames[currentMonth]} {currentYear}
              </h3>
              <button
                onClick={() => navigateMonth('next')}
                className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
              >
                <ChevronRightIcon className="w-5 h-5 text-neutral-700 dark:text-neutral-300" />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Week day headers */}
              {weekDays.map(day => (
                <div key={day} className="text-center text-sm font-medium text-neutral-600 dark:text-neutral-400 py-2">
                  {day}
                </div>
              ))}

              {/* Calendar days */}
              {calendarDays.map((day, index) => {
                const dayEvents = day ? getEventsForDay(day) : [];
                const isToday = day === new Date().getDate() &&
                               currentMonth === new Date().getMonth() &&
                               currentYear === new Date().getFullYear();
                const hasMultipleEvents = dayEvents.length > 2;

                return (
                  <div
                    key={index}
                    onClick={() => day && dayEvents.length > 0 && openDayModal(day)}
                    className={`
                      min-h-[50px] md:min-h-[80px] p-1 md:p-2 border border-neutral-200 dark:border-neutral-700
                      ${day && dayEvents.length > 0 ? 'cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800' : ''}
                      ${day && dayEvents.length === 0 ? 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50' : ''}
                      ${isToday ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-white dark:bg-neutral-900'}
                      transition-colors relative
                    `}
                  >
                    {day && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary-600 dark:text-primary-400' : 'text-neutral-700 dark:text-neutral-300'}`}>
                          {day}
                        </div>
                        {dayEvents.length > 0 && (
                          <div className="space-y-1">
                            {dayEvents.slice(0, 2).map((event, idx) => (
                              <div
                                key={idx}
                                className="text-xs p-1 rounded truncate"
                                style={{
                                  backgroundColor: `${event.color}20`,
                                  color: event.color,
                                  borderLeft: `2px solid ${event.color}`
                                }}
                                title={`${event.title}: ${event.startTime} - ${event.endTime}`}
                              >
                                {event.title}
                              </div>
                            ))}
                            {hasMultipleEvents && (
                              <div className="text-xs text-primary-600 dark:text-primary-400 font-medium hover:underline">
                                +{dayEvents.length - 2} mehr
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Loading/Error States */}
            {loading && (
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-blue-700 dark:text-blue-400">Termine werden geladen...</p>
              </div>
            )}
            
            {error && (
              <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg flex items-start">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-700 dark:text-yellow-400 font-medium">
                    {error}
                  </p>
                  <p className="text-yellow-600 dark:text-yellow-500 text-sm mt-1">
                    Die Standardtermine werden angezeigt.
                  </p>
                </div>
              </div>
            )}

            {/* Legend/Filter */}
            {!loading && allEventTypes.length > 0 && (
              <div className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    Filter (klicken zum Ein-/Ausblenden):
                  </p>
                  {hiddenEventTypes.size > 0 && (
                    <button
                      onClick={() => setHiddenEventTypes(new Set())}
                      className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      Alle anzeigen
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {allEventTypes.map((eventType, idx) => {
                    const isHidden = hiddenEventTypes.has(eventType.title);
                    return (
                      <button
                        key={idx}
                        onClick={() => toggleEventType(eventType.title)}
                        className={`
                          flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all
                          ${isHidden
                            ? 'bg-neutral-100 dark:bg-neutral-800 opacity-50'
                            : 'bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                          }
                        `}
                      >
                        <div
                          className={`w-3 h-3 rounded-full transition-opacity ${isHidden ? 'opacity-30' : ''}`}
                          style={{ backgroundColor: eventType.color }}
                        />
                        <span className={`text-neutral-700 dark:text-neutral-300 ${isHidden ? 'line-through opacity-50' : ''}`}>
                          {eventType.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {hiddenEventTypes.size > 0 && (
                  <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-2">
                    {hiddenEventTypes.size} {hiddenEventTypes.size === 1 ? 'Kategorie' : 'Kategorien'} ausgeblendet
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Events Section */}
      <section className="py-10 md:py-20 bg-white dark:bg-background-dark-secondary">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4 text-neutral-900 dark:text-neutral-100">
              Highlights 2026
            </h2>
            <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
              Die wichtigsten Veranstaltungen des Jahres - von Vereinsfesten über Wettkämpfe bis zu offiziellen Prüfungen
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
            {mainEvents
              .sort((a, b) => {
                // Sort events: upcoming first, then past
                const aDate = `${a.date}.${a.month === 'APR' ? '04' : a.month === 'JUL' ? '07' : a.month === 'SEP' ? '09' : '12'}.${a.year}`;
                const bDate = `${b.date}.${b.month === 'APR' ? '04' : b.month === 'JUL' ? '07' : b.month === 'SEP' ? '09' : '12'}.${b.year}`;
                const aIsPast = isEventPast(aDate);
                const bIsPast = isEventPast(bDate);
                
                if (aIsPast && !bIsPast) return 1;
                if (!aIsPast && bIsPast) return -1;
                return 0;
              })
              .map((event) => {
              const monthMap: Record<string, string> = { JAN: '01', FEB: '02', MAR: '03', APR: '04', MAI: '05', JUN: '06', JUL: '07', AUG: '08', SEP: '09', OKT: '10', NOV: '11', DEZ: '12' };
              const isPast = isEventPast(`${event.date || '01'}.${monthMap[event.month] || '01'}.${event.year}`);
              
              return (
                <div key={event.id} className={`card overflow-hidden transition-all duration-300 relative ${ isPast ? 'opacity-60 hover:opacity-70' : 'hover:shadow-xl' }`}>
                  {isPast && (
                    <div className="absolute top-4 right-4 z-10">
                      <span className="bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                        Vorbei
                      </span>
                    </div>
                  )}
                  <div className="p-3 md:p-6">
                    <div className="flex items-start justify-between mb-3 md:mb-4">
                      <div className={`text-center rounded-lg p-2 md:p-3 ${
                        isPast 
                          ? 'bg-gray-100 dark:bg-gray-900/20' 
                          : 'bg-primary-100 dark:bg-primary-900/20'
                      }`}>
                        <div className={`text-xl md:text-3xl font-bold ${
                          isPast 
                            ? 'text-gray-500 dark:text-gray-400' 
                            : 'text-primary-600 dark:text-primary-400'
                        }`}>
                          {event.date}
                        </div>
                        <div className={`text-sm font-medium ${
                          isPast 
                            ? 'text-gray-500 dark:text-gray-400' 
                            : 'text-primary-600 dark:text-primary-400'
                        }`}>
                          {event.month}
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        isPast 
                          ? 'bg-gray-100 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400' 
                          : getCategoryStyle(event.category)
                      }`}>
                        {event.category}
                      </span>
                    </div>

                    <h3 className={`text-xl font-bold mb-2 ${
                      isPast 
                        ? 'text-gray-600 dark:text-gray-400 line-through' 
                        : 'text-neutral-900 dark:text-neutral-100'
                    }`}>
                      {event.title}
                    </h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">
                      <CalendarIcon className="w-4 h-4 inline mr-1" />
                      {event.fullDate}
                    </p>
                    <p className={`text-sm ${
                      isPast 
                        ? 'text-gray-500 dark:text-gray-500' 
                        : 'text-neutral-600 dark:text-neutral-400'
                    }`}>
                      {event.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Regular Training Times */}
      <section className="py-10 md:py-20 bg-neutral-50 dark:bg-background-dark">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4 text-neutral-900 dark:text-neutral-100">
              Gästetraining
            </h2>
            <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400">
              Kostenlos und unverbindlich - lernen Sie uns kennen!
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
            {/* Saturday Training */}
            <div className="card p-4 md:p-6">
              <div className="flex items-center mb-3 md:mb-4">
                <CalendarDaysIcon className="w-6 h-6 md:w-8 md:h-8 text-primary-600 dark:text-primary-400 mr-2 md:mr-3" />
                <h3 className="text-xl md:text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  Samstag
                </h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-start">
                  <ClockIcon className="w-5 h-5 text-neutral-500 mr-3 mt-0.5" />
                  <div>
                    <p className="font-semibold text-neutral-900 dark:text-neutral-100">
                      9:30 - 11:00 Uhr
                    </p>
                    <p className="text-neutral-600 dark:text-neutral-400">
                      Anfängertraining / Gästetraining
                    </p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-500 mt-1">
                      Perfekt für Interessierte und Einsteiger
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Info */}
            <div className="card p-4 md:p-6 bg-primary-50 dark:bg-primary-900/10 border-primary-200 dark:border-primary-800">
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-neutral-900 dark:text-neutral-100 flex items-center">
                <UserGroupIcon className="w-6 h-6 mr-2 text-primary-600 dark:text-primary-400" />
                Wichtige Informationen
              </h3>
              <ul className="space-y-2 text-neutral-700 dark:text-neutral-300">
                <li className="flex items-start">
                  <ArrowRightIcon className="w-4 h-4 text-primary-600 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Aktuell nur Warteliste – Anmeldung übers Kontaktformular</span>
                </li>
                <li className="flex items-start">
                  <ArrowRightIcon className="w-4 h-4 text-primary-600 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Anmeldung für Gästetraining erwünscht</span>
                </li>
                <li className="flex items-start">
                  <ArrowRightIcon className="w-4 h-4 text-primary-600 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Bei schlechtem Wetter bitte nachfragen</span>
                </li>
                <li className="flex items-start">
                  <ArrowRightIcon className="w-4 h-4 text-primary-600 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Impfnachweis erforderlich</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Location Info */}
      <section className="py-10 md:py-20 bg-white dark:bg-background-dark-secondary">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="card overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="p-5 md:p-8">
                <h3 className="text-2xl font-bold mb-4 text-neutral-900 dark:text-neutral-100 flex items-center">
                  <MapPinIcon className="w-6 h-6 mr-2 text-primary-600 dark:text-primary-400" />
                  Veranstaltungsort
                </h3>
                <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                  Alle Veranstaltungen und Trainings finden auf unserem Vereinsgelände statt:
                </p>
                <address className="not-italic text-neutral-700 dark:text-neutral-300 space-y-2">
                  <p className="font-semibold">HSV Berlin-Adlershof e.V.</p>
                  <p>Birkenweg</p>
                  <p>12439 Berlin</p>
                  <p className="pt-4">
                    <a 
                      href="https://www.google.com/maps/dir/?api=1&destination=Hundesportverein+Berlin-Adlershof+e.V.,Birkenweg,12439+Berlin" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center"
                    >
                      Route planen
                      <ArrowRightIcon className="w-4 h-4 ml-1" />
                    </a>
                  </p>
                </address>
              </div>
              <div className="h-64 md:h-auto">
                <img 
                  src="/images/facilities/training-field-2.jpg" 
                  alt="Vereinsgelände" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Day Events Modal/Slideup */}
      {showDayModal && selectedDay && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={closeDayModal}
          />
          {/* Modal - Slideup on mobile */}
          <div className="fixed inset-x-0 bottom-0 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-50 w-full md:w-auto md:max-w-lg animate-slideup md:animate-none">
            <div className="bg-white dark:bg-surface-dark rounded-t-2xl md:rounded-2xl shadow-xl max-h-[80vh] overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
                <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                  {selectedDay}. {monthNames[currentMonth]} {currentYear}
                </h3>
                <button
                  onClick={closeDayModal}
                  className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full transition-colors"
                >
                  <XMarkIcon className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                </button>
              </div>
              {/* Events List */}
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                <div className="space-y-3">
                  {getEventsForDay(selectedDay).map((event, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-lg"
                      style={{
                        backgroundColor: `${event.color}15`,
                        borderLeft: `4px solid ${event.color}`
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <h4
                          className="font-semibold"
                          style={{ color: event.color }}
                        >
                          {event.title}
                        </h4>
                      </div>
                      <div className="flex items-center mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                        <ClockIcon className="w-4 h-4 mr-1" />
                        {event.startTime.slice(0, 5)} - {event.endTime.slice(0, 5)} Uhr
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Events;